import {
  classifyMediaFailure,
  isRetryableMediaFailure,
  type MediaFailureReason,
} from '@astral/shared';

/**
 * A bounded, self-retrying processing queue for sticker uploads.
 *
 * WHY THIS EXISTS. The previous batch runner fired uploads at a fixed small
 * concurrency and treated EVERY failure identically: one `catch {}` that set
 * the item to "error" and stopped. Combined with a per-user rate limit of ten
 * uploads a minute, a 50-image selection reliably produced ~40 "Failed" tiles
 * that a manual Retry — a minute later, once the window had rolled — would
 * then happily process. That is not a batch pipeline; it is a batch pipeline's
 * failure mode wearing one.
 *
 * The three properties that make a 100-image selection safe:
 *
 *   BOUNDED     — a fixed number of jobs are in flight regardless of how many
 *                 files were selected. Everything else waits as QUEUED, so the
 *                 request count never scales with the selection size.
 *
 *   CLASSIFIED  — a failure is retried only if its reason says another attempt
 *                 could help. A corrupt file fails once and stays failed; a
 *                 429, a timeout or a dropped connection retries itself with
 *                 backoff and jitter, and the user is never asked to do it.
 *
 *   STABLE      — an item's identity is minted ONCE, when it is enqueued, and
 *                 survives every attempt. Together with the server's
 *                 idempotency replay, that is what guarantees a retry can never
 *                 duplicate a sticker, even when the first attempt actually
 *                 succeeded and only its response was lost.
 *
 * Deliberately framework-free: it holds no React state and can be driven and
 * asserted on directly, which is how the 1/10/50/100-image behaviour is tested
 * without rendering anything.
 */

export type UploadItemStatus =
  /** Accepted, waiting for a slot. */
  | 'queued'
  /** An attempt is in flight. */
  | 'processing'
  /** An attempt failed for a retryable reason; the next one is scheduled. */
  | 'retrying'
  /** Succeeded. Terminal. */
  | 'ready'
  /** Permanently failed, or out of automatic attempts. Terminal until a manual retry. */
  | 'failed';

export interface UploadItem<T> {
  /** Stable identity. Minted at enqueue, never re-minted on retry. */
  readonly id: string;
  /** Original position in the user's selection. Never changes. */
  readonly index: number;
  readonly name: string;
  readonly file: File;
  status: UploadItemStatus;
  /** Attempts made so far, including the one in flight. */
  attempts: number;
  /** The structured cause of the latest failure (kept for copy + diagnostics). */
  reason?: MediaFailureReason;
  /** The processed result, once `status === 'ready'`. */
  result?: T;
}

export interface UploadQueueOptions<T> {
  /** How many attempts may run at once. */
  concurrency: number;
  /** Total attempts per item, including the first. */
  maxAttempts: number;
  /** First backoff delay, in ms. Grows by `backoffFactor` per attempt. */
  baseDelayMs: number;
  backoffFactor: number;
  /** Ceiling on a single backoff delay, before jitter. */
  maxDelayMs: number;
  /** Perform one attempt. Rejects to signal failure. */
  run: (item: UploadItem<T>, signal: AbortSignal) => Promise<T>;
  /** Called after every state change, with the full list in selection order. */
  onChange: (items: UploadItem<T>[]) => void;
  /** Injected for tests; defaults to real timers and Math.random. */
  sleep?: (ms: number, signal: AbortSignal) => Promise<void>;
  random?: () => number;
}

/**
 * Defaults, chosen against the measured system rather than picked for roundness.
 *
 * CONCURRENCY 4 — the backend's encode semaphore is itself bounded at 4 (the
 * measured throughput knee: 8 → 28 images/sec across 1 → 4, then flat), so
 * sending more only deepens a server-side queue while consuming browser
 * connections. Browsers cap ~6 concurrent connections per host, so staying at 4
 * also leaves headroom for the autosave and publish-poll requests that run
 * alongside a batch — starving those was how a batch upload used to make the
 * whole editor feel stalled.
 *
 * FOUR ATTEMPTS with a 700 ms base and a 2.5× factor gives roughly
 * 0.7s → 1.8s → 4.4s of backoff, so an item rides out a several-second blip
 * without the user noticing, and a genuinely down backend gives up in about
 * seven seconds rather than hammering.
 */
export const DEFAULT_UPLOAD_CONCURRENCY = 4;
export const DEFAULT_MAX_ATTEMPTS = 4;
export const DEFAULT_BASE_DELAY_MS = 700;
export const DEFAULT_BACKOFF_FACTOR = 2.5;
export const DEFAULT_MAX_DELAY_MS = 8000;

const defaultSleep = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener('abort', () => {
      clearTimeout(timer);
      resolve();
    });
  });

/** Terminal statuses — an item in one of these is not coming back on its own. */
export function isSettled(status: UploadItemStatus): boolean {
  return status === 'ready' || status === 'failed';
}

export class UploadQueue<T> {
  private readonly items: UploadItem<T>[] = [];
  private readonly options: Required<UploadQueueOptions<T>>;
  private readonly controller = new AbortController();
  private active = 0;
  private running = false;
  private idle: (() => void)[] = [];

  constructor(options: UploadQueueOptions<T>) {
    this.options = {
      sleep: defaultSleep,
      random: Math.random,
      ...options,
    };
  }

  /** Items in the user's ORIGINAL selection order, whatever order they finish in. */
  snapshot(): UploadItem<T>[] {
    return this.items.map((item) => ({ ...item }));
  }

  get inFlight(): number {
    return this.active;
  }

  /**
   * Add files. Identity is assigned HERE, once — `id` is what the UI keys on and
   * what the finished sticker inherits, so it must not change when an attempt is
   * retried. Enqueuing more files while the queue is running is safe: they join
   * the same bounded pool rather than starting a second one.
   */
  enqueue(files: { id: string; name: string; file: File }[]): void {
    for (const entry of files) {
      this.items.push({
        id: entry.id,
        index: this.items.length,
        name: entry.name,
        file: entry.file,
        status: 'queued',
        attempts: 0,
      });
    }
    this.publish();
  }

  /** Re-arm every item that has permanently failed (the manual Retry action). */
  retryFailed(): void {
    let armed = false;
    for (const item of this.items) {
      if (item.status !== 'failed') continue;
      item.status = 'queued';
      item.attempts = 0;
      item.reason = undefined;
      armed = true;
    }
    if (armed) this.publish();
  }

  /** Abort everything in flight and stop scheduling. Safe to call twice. */
  cancel(): void {
    this.controller.abort();
  }

  get cancelled(): boolean {
    return this.controller.signal.aborted;
  }

  /**
   * Drain the queue, resolving when every item has settled.
   *
   * Re-entrant by design: calling it again while it is already draining simply
   * waits for the same run, so a manual retry mid-batch cannot start a second
   * set of workers and double the concurrency.
   */
  async run(): Promise<void> {
    if (this.running) {
      await new Promise<void>((resolve) => this.idle.push(resolve));
      return;
    }
    this.running = true;
    try {
      const workers = Array.from({ length: this.options.concurrency }, () => this.worker());
      await Promise.all(workers);
    } finally {
      this.running = false;
      const waiters = this.idle;
      this.idle = [];
      for (const resolve of waiters) resolve();
    }
  }

  /**
   * One worker: take the next queued item, see it through its retries, repeat.
   *
   * The retry loop lives INSIDE the worker rather than re-queueing, which is
   * what keeps `concurrency` an honest bound — a retrying item holds its slot
   * instead of quietly adding a parallel one — and what preserves an item's
   * position: it never loses its place to items enqueued later.
   */
  private async worker(): Promise<void> {
    for (;;) {
      if (this.controller.signal.aborted) return;
      const item = this.items.find((candidate) => candidate.status === 'queued');
      if (!item) return;

      this.active += 1;
      // Claim it synchronously, before any await, so a sibling worker starting
      // in the same tick cannot pick up the same item.
      item.status = 'processing';
      this.publish();
      try {
        await this.attempt(item);
      } finally {
        this.active -= 1;
      }
    }
  }

  /** Run one item to a terminal state. */
  private async attempt(item: UploadItem<T>): Promise<void> {
    for (;;) {
      if (this.controller.signal.aborted) {
        item.status = 'failed';
        item.reason = 'CANCELLED';
        this.publish();
        return;
      }

      item.attempts += 1;
      item.status = 'processing';
      this.publish();

      try {
        item.result = await this.options.run(item, this.controller.signal);
        item.status = 'ready';
        item.reason = undefined;
        this.publish();
        return;
      } catch (error) {
        const reason = toReason(error);
        item.reason = reason;

        const exhausted = item.attempts >= this.options.maxAttempts;
        if (!isRetryableMediaFailure(reason) || exhausted || this.controller.signal.aborted) {
          /*
           * Terminal. A permanent reason stops after ONE attempt — spending the
           * retry budget on a corrupt file is how a 100-image batch turns into
           * 400 pointless requests — and a retryable one stops once the budget
           * is spent, leaving the manual Retry as the user's remaining move.
           */
          item.status = 'failed';
          this.publish();
          return;
        }

        item.status = 'retrying';
        this.publish();
        await this.options.sleep(this.backoffFor(item.attempts), this.controller.signal);
      }
    }
  }

  /**
   * Exponential backoff with FULL jitter.
   *
   * Full jitter (a uniform draw from [0, delay]) rather than a fixed delay
   * because the failures worth retrying are overwhelmingly correlated — a rate
   * limit or an overloaded backend hits the whole batch at once. Retrying all
   * of them at the same instant simply reproduces the burst that caused the
   * failure; spreading them is what lets the queue recover instead of
   * oscillating.
   */
  private backoffFor(attempt: number): number {
    const ceiling = Math.min(
      this.options.maxDelayMs,
      this.options.baseDelayMs * this.options.backoffFactor ** (attempt - 1),
    );
    return Math.round(this.options.random() * ceiling);
  }

  private publish(): void {
    this.options.onChange(this.snapshot());
  }
}

/**
 * Map a thrown value onto a failure reason.
 *
 * Anything that is not a recognisable API error is INTERNAL — permanent — so a
 * bug in the client cannot turn into an automatic retry storm.
 */
function toReason(error: unknown): MediaFailureReason {
  if (typeof error !== 'object' || error === null) return 'INTERNAL';
  const candidate = error as { status?: unknown; code?: unknown; details?: unknown };
  if (typeof candidate.status !== 'number' || typeof candidate.code !== 'string') return 'INTERNAL';
  const details = candidate.details as Record<string, string[]> | undefined;
  return classifyMediaFailure({
    status: candidate.status,
    code: candidate.code,
    // The backend's own classification, when it made one.
    reason: details?.reason?.[0],
  });
}
